import { ArrowLeft, Building2, CalendarDays, ChevronDown, ChevronRight, Download, FolderOpenDot, Handshake, ListChecks, LogOut, Medal, MoreHorizontal, Newspaper, Shield, Swords, Trash2 } from "lucide-react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useRef, useState } from "react";

import { useAuth } from "@/context/AuthContext";
import InstallModal from "@/components/InstallModal";
import MobileNavigation from "@/components/MobileNavigation";
import { api } from "@/lib/api";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_fight-bracket-pro/artifacts/bcn58mfl_WBCGermany.png";
const items = [
  { to: "/events", label: "Events", icon: CalendarDays },
  { to: "/calendar", label: "Kalender", icon: CalendarDays },
  { to: "/news", label: "News", icon: Newspaper },
  { to: "/projekte", label: "Projekte", icon: FolderOpenDot },
  { to: "/sponsoren", label: "Sponsoren", icon: Handshake },
  { to: "/my-fights", label: "Meine Kämpfe", icon: Swords },
  { to: "/rankings", label: "Rangliste", icon: Medal },
  { to: "/gyms", label: "WBC Gyms", icon: Building2 },
  { to: "/settings", label: "Mehr", icon: MoreHorizontal },
];

const adminItems = [
  { to: "/calendar/admin", label: "Kalender pflegen", icon: CalendarDays, superOnly: true },
  { to: "/news-admin", label: "News pflegen", icon: Newspaper },
  { to: "/projekte-admin", label: "Projekte pflegen", icon: FolderOpenDot },
  { to: "/sponsoren-admin", label: "Sponsoren pflegen", icon: Handshake },
  { to: "/settings/rankings", label: "Rangliste pflegen", icon: Medal },
  { to: "/gyms-admin", label: "WBC Gyms pflegen", icon: Building2 },
  { to: "/settings/weight-classes", label: "Gewichtsklassen", icon: ListChecks },
  { to: "/settings/users", label: "Benutzer & Rollen", icon: MoreHorizontal, superOnly: true },
];

const ADMIN_COLLAPSED_KEY = "wbc_admin_group_collapsed_v1";

export default function AppShell() {
  const { user, logout } = useAuth();
  const [installOpen, setInstallOpen] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const hasInteracted = useRef(false);
  const navigate = useNavigate();
  const location = useLocation();
  const eventMatch = location.pathname.match(/^\/events\/([^/]+)$/);
  const canManageEvents = ["super_admin", "event_admin"].includes(user?.role);
  const isSuper = user?.role === "super_admin";
  const visibleAdminItems = adminItems.filter((i) => !i.superOnly || isSuper);
  const [adminCollapsed, setAdminCollapsed] = useState(() => {
    if (typeof window === "undefined") return true;
    const stored = window.localStorage.getItem(ADMIN_COLLAPSED_KEY);
    return stored === null ? true : stored === "true";
  });
  const toggleAdmin = () => {
    setAdminCollapsed((current) => {
      const next = !current;
      try { window.localStorage.setItem(ADMIN_COLLAPSED_KEY, String(next)); } catch { /* Storage evtl. blockiert */ }
      return next;
    });
  };

  useEffect(() => {
    const listener = (event) => { event.preventDefault(); setDeferredPrompt(event); };
    const interactionListener = () => { hasInteracted.current = true; };
    window.addEventListener("beforeinstallprompt", listener);
    window.addEventListener("pointerdown", interactionListener, { once: true });
    const dismissed = localStorage.getItem("wbc_install_prompt_dismissed_v2");
    const timer = !dismissed ? setTimeout(() => {
      if (!hasInteracted.current) setInstallOpen(true);
    }, 3500) : null;
    return () => { window.removeEventListener("beforeinstallprompt", listener); window.removeEventListener("pointerdown", interactionListener); if (timer) clearTimeout(timer); };
  }, []);

  const closeInstall = () => {
    localStorage.setItem("wbc_install_prompt_dismissed_v2", "true");
    setInstallOpen(false);
  };

  const signOut = async () => { await logout(); navigate("/"); };
  const deleteEvent = async () => {
    if (!eventMatch || !window.confirm("Dieses Event inklusive Kämpfern, Kategorien und Kämpfen endgültig löschen?")) return;
    await api.delete(`/events/${eventMatch[1]}`);
    navigate("/events");
  };

  return (
    <div className="app-shell" data-testid="app-shell">
      {location.pathname !== "/events" && <div className="global-page-actions"><button className="global-back-button" onClick={() => navigate(-1)} data-testid="global-back-button"><ArrowLeft size={18} /> ZURÜCK</button>{eventMatch && canManageEvents && <button className="global-delete-button" onClick={deleteEvent} data-testid="delete-event-button" title="Event endgültig löschen"><Trash2 size={18} /><span>EVENT LÖSCHEN</span></button>}</div>}
      <aside className="sidebar" data-testid="sidebar">
        <div className="brand-lockup"><img src={LOGO_URL} alt="WBC Germany" data-testid="navigation-logo" /><span>WBC<br />MUAY THAI</span></div>
        <div className="sidebar-scroll" data-testid="sidebar-scroll">
          <nav>{items.map(({ to, label, icon: Icon }) => <NavLink key={to} to={to} className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`} data-testid={`sidebar-${label.toLowerCase().replace(" ", "-")}-link`}><Icon size={19} /><span>{label}</span></NavLink>)}</nav>
          {canManageEvents && <div className={`admin-quick-nav ${adminCollapsed ? "collapsed" : "expanded"}`} data-testid="admin-quick-navigation">
            <button type="button" className="admin-quick-toggle" onClick={toggleAdmin} aria-expanded={!adminCollapsed} data-testid="admin-quick-toggle"><Shield size={16} /><span>Admin</span>{adminCollapsed ? <ChevronRight size={16} /> : <ChevronDown size={16} />}</button>
            {!adminCollapsed && <div className="admin-quick-list" data-testid="admin-quick-list">
              {visibleAdminItems.map(({ to, label, icon: Icon }) => <NavLink key={to} to={to} className="nav-link" data-testid={`admin-quick-${to.replace(/[^a-z]/gi, "-")}-link`}><Icon size={18} /><span>{label}</span></NavLink>)}
            </div>}
          </div>}
        </div>
        <div className="sidebar-bottom" data-testid="sidebar-bottom"><div className="user-chip" data-testid="current-user-info"><span className="role-dot" /> <div><b>{user?.full_name}</b><small>{user?.role?.replace("_", " ")}</small></div></div><button className="nav-link" onClick={() => setInstallOpen(true)} data-testid="sidebar-install-app-button"><Download size={19} /><span>App speichern</span></button><button className="nav-link" onClick={signOut} data-testid="logout-button"><LogOut size={19} /><span>Abmelden</span></button></div>
      </aside>

      <MobileNavigation />

      <main className="page-main" key={location.pathname}><Outlet context={{ openInstall: () => setInstallOpen(true), user }} /></main>
      <InstallModal open={installOpen} onClose={closeInstall} deferredPrompt={deferredPrompt} />
    </div>
  );
}
